/* YAGA strain picker: one row per strain, chips for its sizes or types, a running summary and multi-add. */
if (!customElements.get('yaga-ounce-picker')) {
  customElements.define(
    'yaga-ounce-picker',
    class YagaOuncePicker extends HTMLElement {
      connectedCallback() {
        this.rows = Array.from(this.querySelectorAll('[data-yop-row]'));
        if (!this.rows.length) return;

        /* Every choice a row can show: price, copy, photo and the option values that drive the gallery. */
        this.choices = new Map();
        this.querySelectorAll('[data-yop-choice-data]').forEach((template) => {
          const data = template.dataset;
          this.choices.set(data.yopChoiceData, {
            price: Number(data.price) || 0,
            compare: Number(data.compare) || 0,
            save: Number(data.save) || 0,
            meta: data.meta || '',
            now: data.now || '',
            was: data.was || '',
            axis: data.axis || '',
            option1: data.option1 || '',
            option2: data.option2 || '',
            html: template.innerHTML,
            nodes: null,
          });
        });

        this.rows.forEach((row) => {
          row.yopChoices = Array.from(row.querySelectorAll('[data-yop-choice]')).map((chip) => chip.dataset.variantId);
          if (!row.yopChoices.length) row.yopChoices = [row.dataset.variantId];
        });

        this.quantities = new Map();
        this.filters = { tier: 'all', type: 'all', axis: 'all' };
        this.threshold = Number(this.dataset.threshold || 0);
        this.cartTotal = 0;
        this.busy = false;
        this.money = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        });

        this.titleEl = this.querySelector('[data-yop-title]');
        this.subtitleEl = this.querySelector('[data-yop-subtitle]');
        this.addButton = this.querySelector('[data-yop-add]');
        this.errorEl = this.querySelector('[data-yop-error]');
        this.emptyTitle = this.titleEl.textContent;
        this.emptySubtitle = this.subtitleEl.textContent;

        this.addEventListener('click', this.onClick.bind(this));
        this.closest('.product__info-container')?.classList.add('yop-active');

        this.rows.forEach((row) => this.showChoice(row, row.dataset.variantId));
        this.refreshCartTotal();
        this.openFromUrl();
        this.render();
      }

      onClick(event) {
        const choice = event.target.closest('[data-yop-choice]');
        const plus = event.target.closest('[data-yop-plus]');
        const minus = event.target.closest('[data-yop-minus]');
        const main = event.target.closest('[data-yop-main]');
        const filter = event.target.closest('[data-yop-filter]');

        if (choice) return this.pickChoice(choice.closest('[data-yop-row]'), choice.dataset.variantId);
        if (plus) return this.changeQuantity(plus.closest('[data-yop-row]'), 1);
        if (minus) return this.changeQuantity(minus.closest('[data-yop-row]'), -1);
        if (main) return this.toggleDetails(main.closest('[data-yop-row]'));
        if (filter) return this.setFilter(filter.dataset.yopFilter, filter.dataset.value);
        if (event.target.closest('[data-yop-reset]')) {
          this.setFilter('tier', 'all');
          this.setFilter('type', 'all');
          return this.setFilter('axis', 'all');
        }
        if (event.target.closest('[data-yop-add]')) return this.addToCart();
      }

      /* Swap the row over to one of its variants: price, saving, photo, description and the picked count. */
      showChoice(row, id) {
        const data = this.choices.get(id);
        if (!data) return;
        row.dataset.variantId = id;

        if (!data.nodes) {
          const holder = document.createElement('div');
          holder.innerHTML = data.html;
          data.nodes = holder;
        }

        const thumb = row.querySelector('[data-yop-thumb]');
        const image = data.nodes.querySelector('img');
        if (thumb) thumb.replaceChildren(image ? image.cloneNode(true) : '');

        const details = row.querySelector('[data-yop-details]');
        const copy = data.nodes.querySelector('.yop__details');
        if (details) {
          details.innerHTML = copy ? copy.innerHTML : '';
          if (!copy) details.hidden = true;
        }

        row.querySelector('[data-yop-meta]').textContent = data.meta;
        row.querySelector('[data-yop-now]').textContent = data.now;

        const was = row.querySelector('[data-yop-was]');
        const save = row.querySelector('[data-yop-save]');
        was.textContent = data.save > 0 ? data.was : '';
        was.hidden = data.save === 0;
        save.textContent = data.save > 0 ? `Save ${data.save}%` : '';
        save.hidden = data.save === 0;

        row.querySelectorAll('[data-yop-choice]').forEach((chip) => {
          chip.setAttribute('aria-pressed', String(chip.dataset.variantId === id));
        });
      }

      pickChoice(row, id) {
        this.showChoice(row, id);
        if (row.classList.contains('is-open')) {
          this.setOpen(row, true);
          this.syncNativePicker(row);
        }
        this.render();
      }

      changeQuantity(row, delta) {
        const id = row.dataset.variantId;
        const next = Math.max(0, Math.min(99, (this.quantities.get(id) || 0) + delta));
        if (next === 0) this.quantities.delete(id);
        else this.quantities.set(id, next);
        this.clearError();
        this.render();
        if (next === 0) row.querySelector('[data-yop-plus]').focus();
      }

      toggleDetails(row) {
        const button = row.querySelector('[data-yop-main]');
        const opening = button.getAttribute('aria-expanded') !== 'true';
        this.rows.forEach((other) => this.setOpen(other, false));
        this.setOpen(row, opening);
        if (opening) this.syncNativePicker(row);
      }

      setOpen(row, open) {
        const details = row.querySelector('[data-yop-details]');
        row.querySelector('[data-yop-main]').setAttribute('aria-expanded', String(open));
        row.classList.toggle('is-open', open);
        if (details) details.hidden = !open || details.innerHTML.trim() === '';
      }

      openFromUrl() {
        const id = new URLSearchParams(window.location.search).get('variant');
        if (!id) return;
        const row = this.rows.find((candidate) => candidate.yopChoices.includes(id));
        if (!row) return;
        this.showChoice(row, id);
        this.setOpen(row, true);
      }

      /* Drives the theme's own (hidden) variant picker so the gallery photo and URL follow the row. */
      syncNativePicker(row) {
        const data = this.choices.get(row.dataset.variantId);
        if (!data) return;

        const choose = (index, value) => {
          const fieldset = document.querySelectorAll('variant-selects fieldset')[index];
          if (!fieldset || !value) return false;
          const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
            (candidate) => candidate.value === value
          );
          if (!input || input.checked) return false;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        };

        const changedFirst = choose(0, data.option1);
        if (!data.option2) return;
        if (!changedFirst) {
          choose(1, data.option2);
          return;
        }
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            unsubscribe();
            choose(1, data.option2);
          });
        }
      }

      setFilter(kind, value) {
        this.filters[kind] = value;
        this.querySelectorAll(`[data-yop-filter="${kind}"]`).forEach((chip) => {
          chip.setAttribute('aria-pressed', String(chip.dataset.value === value));
        });

        /* A size filter also switches every matching row over to that size, so the prices compare like for like. */
        if (kind === 'axis' && value !== 'all') {
          this.rows.forEach((row) => {
            const match = row.yopChoices.find((id) => this.choices.get(id)?.axis === value);
            if (match) this.showChoice(row, match);
          });
        }

        let visible = 0;
        this.querySelectorAll('[data-yop-tier]').forEach((tier) => {
          const tierMatches = this.filters.tier === 'all' || this.filters.tier === tier.dataset.yopTier;
          let tierVisible = 0;
          tier.querySelectorAll('[data-yop-row]').forEach((row) => {
            const typeMatches = this.filters.type === 'all' || this.filters.type === row.dataset.type;
            const axisMatches =
              this.filters.axis === 'all' ||
              row.yopChoices.some((id) => this.choices.get(id)?.axis === this.filters.axis);
            const show = tierMatches && typeMatches && axisMatches;
            row.hidden = !show;
            if (show) tierVisible += 1;
          });
          tier.hidden = tierVisible === 0;
          visible += tierVisible;
        });
        this.querySelector('[data-yop-nomatch]').hidden = visible > 0;
        this.render();
      }

      get selection() {
        let count = 0;
        let total = 0;
        let regular = 0;
        this.quantities.forEach((quantity, id) => {
          const data = this.choices.get(id);
          if (!data) return;
          count += quantity;
          total += quantity * data.price;
          regular += quantity * (data.compare || data.price);
        });
        return { count, total, regular };
      }

      render() {
        this.rows.forEach((row) => {
          const quantity = this.quantities.get(row.dataset.variantId) || 0;
          row.classList.toggle('is-picked', quantity > 0);
          row.querySelector('[data-yop-count]').textContent = quantity > 0 ? quantity : '';
          row.querySelector('[data-yop-minus]').hidden = quantity === 0;

          /* A strain can be picked in two sizes at once, so mark the chips that already carry a quantity. */
          row.querySelectorAll('[data-yop-choice]').forEach((chip) => {
            const picked = this.quantities.get(chip.dataset.variantId) || 0;
            chip.classList.toggle('is-picked', picked > 0);
            if (picked > 0) chip.dataset.count = picked;
            else delete chip.dataset.count;
          });
        });

        const { count, total, regular } = this.selection;
        this.classList.toggle('has-selection', count > 0);
        /* This bar adds the picked strains with its own request, so Essential Upsell never saw a click
           on a button it recognises and the ticked "Don't forget these" add-ons were silently dropped.
           This class is the app's own hook for a custom Add to cart button: on a click it adds the
           ticked add-ons with its discount properties, then re-clicks this button so the strains go
           in too. Only while something is picked - otherwise an add-on would go in on its own while
           the bar is telling the customer to pick a strain first. */
        this.addButton.classList.toggle('essential-upsell-product-addon-add-to-cart-button', count > 0);
        if (count === 0) {
          this.titleEl.textContent = this.emptyTitle;
          this.subtitleEl.textContent = this.emptySubtitle;
          this.addButton.textContent = 'Add to cart';
          return;
        }

        const notes = [];
        if (regular > total) notes.push(`You save ${this.money.format((regular - total) / 100)}`);
        if (this.threshold > 0) {
          const remaining = this.threshold - (total + this.cartTotal);
          notes.push(
            remaining <= 0 ? 'Free shipping unlocked' : `Add ${this.money.format(remaining / 100)} more for free shipping`
          );
        }
        this.titleEl.textContent = `${count} ${count === 1 ? 'item' : 'items'} · ${this.money.format(total / 100)}`;
        this.subtitleEl.textContent = notes.join(' · ');
        this.addButton.textContent = `Add ${count} to cart`;
      }

      async cartCount() {
        try {
          const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
          const cart = await response.json();
          return Number(cart.item_count) || 0;
        } catch (error) {
          return 0;
        }
      }

      /* A rejected add returns no section markup, so fetch the drawer's sections and render those. */
      async refreshDrawer(drawer) {
        try {
          const ids = drawer.getSectionsToRender().map((section) => section.id);
          const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
          const sections = await response.json();
          drawer.classList.remove('is-empty');
          drawer.renderContents({ sections });
        } catch (error) {
          /* Leave the cart alone rather than half-render it; the message still tells the shopper. */
        }
      }

      async refreshCartTotal() {
        try {
          const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
          const cart = await response.json();
          this.cartTotal = Number(cart.total_price) || 0;
          this.render();
        } catch (error) {
          this.cartTotal = 0;
        }
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      clearError() {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      async addToCart() {
        if (this.busy) return;
        const items = Array.from(this.quantities, ([id, quantity]) => ({ id: Number(id), quantity }));
        if (!items.length) {
          this.showError('Tap + next to a strain to add it first.');
          this.rows.find((row) => !row.hidden)?.querySelector('[data-yop-plus]')?.focus();
          return;
        }

        const drawer = document.querySelector('cart-drawer');
        const body = { items };
        if (drawer) {
          body.sections = drawer.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        const countBefore = await this.cartCount();

        this.busy = true;
        this.clearError();
        this.addButton.setAttribute('aria-busy', 'true');
        this.addButton.classList.add('is-loading');

        try {
          const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body),
          });
          const data = await response.json();
          if (!response.ok || data.status) {
            /* Shopify refuses the request as a whole but still adds whatever it had room for, so ask
               the cart what really happened instead of assuming nothing did. */
            const added = (await this.cartCount()) > countBefore;
            // Shopify's message can name the remaining stock; never show counts.
            const yagaMessage = data.description || data.message || '';
            const yagaMentionsStock = /\d/.test(yagaMessage) && /(cart|add|available|stock|left|remain)/i.test(yagaMessage);
            const yagaGeneric = added
              ? "We could only add some of those. Your cart has what's available."
              : "Sorry, we can't add more of this item right now.";
            this.showError(!yagaMessage || yagaMentionsStock ? yagaGeneric : yagaMessage);
            if (added) {
              this.quantities.clear();
              this.render();
              if (drawer) await this.refreshDrawer(drawer);
              this.refreshCartTotal();
            }
            return;
          }

          this.quantities.clear();
          this.render();
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'yaga-ounce-picker', cartData: data });
          }
          const quickAddModal = this.closest('quick-add-modal');
          if (drawer && data.sections) {
            /* Dawn's product form clears this flag after the first add; without it the drawer traps focus on a missing node. */
            drawer.classList.remove('is-empty');
            if (quickAddModal) {
              /* Same order as the theme's product form: close the quick-add pop-up, then open the drawer. */
              document.body.addEventListener('modalClosed', () => setTimeout(() => drawer.renderContents(data)), { once: true });
              quickAddModal.hide(true);
            } else {
              drawer.setActiveElement?.(this.addButton);
              drawer.renderContents(data);
            }
          } else {
            window.location.assign(this.dataset.cartUrl);
          }
          this.refreshCartTotal();
        } catch (error) {
          this.showError('Something went wrong adding to your cart. Try again.');
        } finally {
          this.busy = false;
          this.addButton.removeAttribute('aria-busy');
          this.addButton.classList.remove('is-loading');
        }
      }
    }
  );
}
